import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { sort } from '@ctw/shared/utils/sort';
import { ValueSets } from '@ctw/shared/utils/value-sets';
import { hasMatchingCode } from '../codeable-concept';
import { SYSTEM_LOINC } from '../system-urls';
import type { EncounterModel } from './encounter';
import type { ObservationModel } from './observation';

export type Vital = {
  isFirstParty: boolean;
  observation: ObservationModel;
};

function loincCodesFromValueSet(key: keyof typeof ValueSets): Array<string> {
  return ValueSets[key].content.filter((c) => c.system === SYSTEM_LOINC).map((c) => c.code);
}

export class VitalsBucket {
  private vitals: Array<ObservationModel>;

  private readonly builderId: string;

  private readonly encounter?: EncounterModel;

  public constructor(
    vitals: Array<ObservationModel>,
    builderId: string,
    encounter?: EncounterModel,
  ) {
    this.vitals = vitals;
    this.builderId = builderId;
    this.encounter = encounter;
  }

  public filter(predicate: (vital: ObservationModel) => boolean): VitalsBucket {
    const filteredVitals = this.vitals.filter(predicate);
    return new VitalsBucket(filteredVitals, this.builderId, this.encounter);
  }

  public get key() {
    return this.encounter?.id || this.vitals[0]?.id || '';
  }

  public get hasAnyFirstParty() {
    return !!(
      this.bmi?.isFirstParty ||
      this.bloodPressure?.isFirstParty ||
      this.headCircumference?.isFirstParty ||
      this.height?.isFirstParty ||
      this.lmp?.isFirstParty ||
      this.oxygenSaturation?.isFirstParty ||
      this.pain?.isFirstParty ||
      this.pulse?.isFirstParty ||
      this.respiratoryRate?.isFirstParty ||
      this.temperature?.isFirstParty ||
      this.weight?.isFirstParty ||
      this.lmp?.isFirstParty
    );
  }

  public get dateDisplay() {
    return this.dateTime ? formatFHIRDate(this.dateTime) : undefined;
  }

  // Our dateTime is based on the encounter date, if available, or the first vital date.
  public get dateTime() {
    if (this.encounter) {
      return this.encounter.resource.period?.start;
    }

    return this.vitals[0].date;
  }

  public get binaryId() {
    if (this.encounter && this.encounter.docsAndNotes.length > 0) {
      return this.encounter.docsAndNotes[0].binaryId;
    }

    return this.vitals.find((v) => v.binaryId)?.binaryId;
  }

  public get binaryIdSourceModel() {
    if (this.encounter && this.encounter.docsAndNotes.length > 0) {
      return this.encounter;
    }

    return this.vitals.find((v) => v.binaryId);
  }

  public get bmi() {
    const vital = this.getVitalObservation(loincCodesFromValueSet('bmi'));
    if (!vital) {
      return undefined;
    }

    return {
      isFirstParty: vital.isFirstParty,
      observation: vital.observation,
    };
  }

  public get bloodPressure() {
    const systolicCodes = loincCodesFromValueSet('bpSystolic');
    const diastolicCodes = loincCodesFromValueSet('bpDiastolic');
    const combinedCodes = loincCodesFromValueSet('bpCombined');

    const systolicObservation = this.getVitalObservation(systolicCodes);
    const diastolicObservation = this.getVitalObservation(diastolicCodes);
    const combinedObservation = this.getVitalObservation(combinedCodes, true);

    let systolicValue = systolicObservation?.observation.valueWithoutUnits;
    let diastolicValue = diastolicObservation?.observation.valueWithoutUnits;
    let isFirstParty = !!systolicObservation?.isFirstParty && !!diastolicObservation?.isFirstParty;

    // if we don't have a systolic or diastolic observation, or the combined observation is first party
    // then we use the combined observation
    if (!(systolicObservation && diastolicObservation) || combinedObservation?.isFirstParty) {
      systolicValue = combinedObservation?.observation.resource.component
        ?.find((c) =>
          c.code.coding?.some(
            (coding) =>
              coding.code && coding.system === SYSTEM_LOINC && systolicCodes.includes(coding.code),
          ),
        )
        ?.valueQuantity?.value?.toString();
      diastolicValue = combinedObservation?.observation.resource.component
        ?.find((c) =>
          c.code.coding?.some(
            (coding) =>
              coding.code && coding.system === SYSTEM_LOINC && diastolicCodes.includes(coding.code),
          ),
        )
        ?.valueQuantity?.value?.toString();
      isFirstParty = !!combinedObservation?.isFirstParty;
    }

    if (systolicValue && diastolicValue) {
      return {
        display: `${systolicValue} / ${diastolicValue}`,
        systolic: systolicValue,
        diastolic: diastolicValue,
        isFirstParty,
      };
    }

    return undefined;
  }

  public get systolic() {
    const systolic = this.getVitalObservation(loincCodesFromValueSet('bpSystolic'));

    if (!systolic) {
      return undefined;
    }

    return {
      isFirstParty: systolic.isFirstParty,
      observation: systolic.observation,
    };
  }

  public get diastolic() {
    const diastolic = this.getVitalObservation(loincCodesFromValueSet('bpDiastolic'));
    if (!diastolic) {
      return undefined;
    }

    return {
      isFirstParty: diastolic.isFirstParty,
      observation: diastolic.observation,
    };
  }

  public get headCircumference() {
    const headCircumference = this.getVitalObservation(loincCodesFromValueSet('headCircumference'));
    if (!headCircumference) {
      return undefined;
    }

    return {
      isFirstParty: headCircumference.isFirstParty,
      observation: headCircumference.observation,
    };
  }

  public get height() {
    const height = this.getVitalObservation(loincCodesFromValueSet('height'));
    if (!height) {
      return undefined;
    }

    return {
      isFirstParty: height.isFirstParty,
      observation: height.observation,
    };
  }

  // Special key for filtering. See utils/filters.ts.
  public get isHidden() {
    return !!this.encounter?.isHospitalEncounter;
  }

  public get lmp() {
    const lmp = this.getVitalObservation(loincCodesFromValueSet('lmp'));
    if (!lmp) {
      return undefined;
    }

    return {
      isFirstParty: lmp.isFirstParty,
      observation: lmp.observation,
    };
  }

  public get location() {
    return this.encounter?.location.join(', ');
  }

  public get oxygenSaturation() {
    const oxygenSaturation = this.getVitalObservation(loincCodesFromValueSet('o2'));
    if (!oxygenSaturation) {
      return undefined;
    }

    return {
      isFirstParty: oxygenSaturation.isFirstParty,
      observation: oxygenSaturation.observation,
    };
  }

  public get pain() {
    const pain = this.getVitalObservation(loincCodesFromValueSet('pain'));
    if (!pain) {
      return undefined;
    }

    return {
      isFirstParty: pain.isFirstParty,
      observation: pain.observation,
    };
  }

  public get pulse() {
    const pulse = this.getVitalObservation(loincCodesFromValueSet('heartRate'));
    if (!pulse) {
      return undefined;
    }

    return {
      isFirstParty: pulse.isFirstParty,
      observation: pulse.observation,
    };
  }

  public get respiratoryRate() {
    const respiratoryRate = this.getVitalObservation(loincCodesFromValueSet('respiratoryRate'));
    if (!respiratoryRate) {
      return undefined;
    }

    return {
      isFirstParty: respiratoryRate.isFirstParty,
      observation: respiratoryRate.observation,
    };
  }

  public get temperature() {
    const temperature = this.getVitalObservation(loincCodesFromValueSet('temperature'));
    if (!temperature) {
      return undefined;
    }

    return {
      isFirstParty: temperature.isFirstParty,
      observation: temperature.observation,
    };
  }

  public get typeDisplay() {
    return this.encounter?.typeDisplay;
  }

  public get weight() {
    const weight = this.getVitalObservation(loincCodesFromValueSet('weight'));
    if (!weight) {
      return undefined;
    }

    return {
      isFirstParty: weight.isFirstParty,
      observation: weight.observation,
    };
  }

  private getVitalObservation(
    codes: Array<string>,
    includeComponent = false,
  ): { observation: ObservationModel; isFirstParty: boolean } | undefined {
    const matchingObservationsDesc = sort(
      this.vitals.filter(
        (v) =>
          hasMatchingCode(SYSTEM_LOINC, codes, v.resource.code) &&
          (v.valueWithUnits !== '' ||
            !!v.resource.valueQuantity?.value ||
            (includeComponent && !!v.resource.component)),
      ),
      (v) => v.date,
      'desc',
      true,
    );

    if (matchingObservationsDesc.length === 0) {
      return undefined;
    }

    // most recent 1st party vital, else most recent 3rd party
    const firstPartyObservationMaybe = matchingObservationsDesc.find((v) =>
      v.ownedByBuilder(this.builderId),
    );
    const observation = firstPartyObservationMaybe ?? matchingObservationsDesc[0];
    const isFirstParty = observation.ownedByBuilder(this.builderId);

    return { observation, isFirstParty };
  }
}
